import { useEffect, useState } from "react";
import { getStockNews } from "./StockNewsService";
import { getPositionSnapshot } from "./StockTradeService";
import { getWatchlist } from "./StockWatchService";

const formatDate = (str) => {
  if (!str) return "";
  try {
    return new Date(str).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return str;
  }
};

export default function NewsTab() {
  const [news, setNews] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("ALL");
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    loadAndFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAndFetch = async () => {
    const [snapshot, watchlist] = await Promise.all([
      getPositionSnapshot().catch(() => []),
      getWatchlist().catch(() => []),
    ]);
    const fromPositions = (Array.isArray(snapshot) ? snapshot : []).map(p => p.symbol).filter(Boolean);
    const fromWatch = (Array.isArray(watchlist) ? watchlist : []).map(w => w.symbol).filter(Boolean);
    const unique = [...new Set([...fromPositions, ...fromWatch])];
    setSymbols(unique);
    if (unique.length) {
      await fetchNews(unique);
    }
  };

  const fetchNews = async (symbolList) => {
    setLoading(true);
    setError("");
    try {
      const data = await getStockNews(symbolList);
      setNews(data || []);
      setLastRefresh(new Date());
    } catch {
      setError("Failed to load news. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    if (symbols.length) fetchNews(symbols);
  };

  const filtered = selectedSymbol === "ALL"
    ? news
    : news.filter(n => n.symbol === selectedSymbol);

  const countForSymbol = (sym) => news.filter(n => n.symbol === sym).length;

  return (
    <div className="stocks-section">
      {/* Toolbar */}
      <div className="news-toolbar">
        <div className="news-sym-row">
          <button
            className={`news-sym-btn${selectedSymbol === "ALL" ? " active" : ""}`}
            onClick={() => setSelectedSymbol("ALL")}
          >
            All ({news.length})
          </button>
          {symbols.map(s => (
            <button
              key={s}
              className={`news-sym-btn${selectedSymbol === s ? " active" : ""}`}
              onClick={() => setSelectedSymbol(s)}
            >
              {s}
              {countForSymbol(s) > 0 && <span className="news-sym-count">{countForSymbol(s)}</span>}
            </button>
          ))}
        </div>
        <div className="news-toolbar-right">
          {lastRefresh && (
            <span className="news-refresh-time">
              Updated {lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button className="btn btn-secondary" onClick={refresh} disabled={loading || !symbols.length}>
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="stocks-error">{error}</div>}

      {loading && (
        <div className="news-loading">
          {symbols.map(s => (
            <div key={s} className="news-loading-item">
              <span className="news-loading-badge">{s}</span>
              <span className="news-loading-bar" />
            </div>
          ))}
        </div>
      )}

      {!loading && symbols.length === 0 && (
        <div className="news-empty">
          <div className="news-empty-icon">📰</div>
          <div className="news-empty-title">No stocks to show news for</div>
          <div className="news-empty-sub">Add stocks to your portfolio or watchlist to see relevant news here.</div>
        </div>
      )}

      {!loading && symbols.length > 0 && filtered.length === 0 && !error && (
        <div className="news-empty">
          <div className="news-empty-icon">🔍</div>
          <div className="news-empty-title">No news found</div>
          <div className="news-empty-sub">Could not find recent news for the selected stocks.</div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="news-grid">
          {filtered.map((item, idx) => (
            <a
              key={`${item.symbol}-${idx}`}
              className="news-card"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="news-card-meta">
                <span className="news-symbol-badge">{item.symbol}</span>
                {item.source && <span className="news-source">{item.source}</span>}
                <span className="news-date">{formatDate(item.pubDate)}</span>
              </div>
              <div className="news-card-title">{item.title}</div>
              {item.snippet && (
                <div className="news-card-snippet">
                  {item.snippet.length > 160 ? item.snippet.slice(0, 160) + "…" : item.snippet}
                </div>
              )}
              <div className="news-card-cta">Read article →</div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
